import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  connectAuthEmulator,
  getAuth,
  type Auth,
} from 'firebase/auth';
import {
  connectFirestoreEmulator,
  getFirestore,
  type Firestore,
} from 'firebase/firestore';
import {
  connectFunctionsEmulator,
  getFunctions,
  type Functions,
} from 'firebase/functions';

/**
 * Firebase client wiring. Only initialised when demo mode is off.
 * All values come from environment variables — never hardcode credentials.
 */

export interface FirebaseBundle {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  fns: Functions;
}

export function isDemoMode(): boolean {
  const flag = import.meta.env.VITE_DEMO_MODE;
  if (flag === 'true') return true;
  if (flag === 'false') return false;
  // Default: demo when no Firebase project is configured.
  return !import.meta.env.VITE_FIREBASE_PROJECT_ID;
}

let bundle: FirebaseBundle | null = null;

export function getFirebase(): FirebaseBundle {
  if (bundle) return bundle;
  const config = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };
  if (!config.projectId) {
    throw new Error(
      'Firebase is not configured. Set VITE_FIREBASE_* in app/.env or use demo mode.',
    );
  }
  const app = initializeApp(config);
  const auth = getAuth(app);
  const db = getFirestore(app);
  const fns = getFunctions(app, 'asia-south1');

  if (import.meta.env.VITE_USE_EMULATORS === 'true') {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    connectFunctionsEmulator(fns, '127.0.0.1', 5001);
  }

  bundle = { app, auth, db, fns };
  return bundle;
}
